/**
 * DOB + age utilities.
 *
 * IMPORTANT (matches legacy behavior):
 * - Age is calculated in **whole years** (i.e. floor), based on whether the birthday has occurred yet.
 *   This yields boundaries like:
 *   - 16 years 0 months 0 days (on the 16th birthday)
 *   - 15 years 11 months 29 days (day before the 16th birthday)
 *   - 105 years 11 months 29 days (day before the 106th birthday)
 *
 * NOTE on leap-day birthdays (Feb 29):
 * This implementation treats the birthday as occurring on Mar 1 in non‑leap years
 * (same outcome as the existing month/day comparison logic used across the codebase).
 */

export type DobInput = string | Date | null | undefined;

function isValidDate(d: Date): boolean {
  return d instanceof Date && Number.isFinite(d.getTime());
}

/**
 * Parse an input into a Date.
 * - Supports Date objects
 * - Supports ISO date-only strings ("YYYY-MM-DD") as **local** dates (avoids UTC day-shift bugs)
 * - Falls back to Date.parse for full ISO datetime strings
 */
export function parseDobToDate(value: DobInput): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) {
    return isValidDate(value) ? value : undefined;
  }
  if (typeof value !== "string") return undefined;

  const raw = value.trim();
  if (!raw) return undefined;

  // Date-only ISO (common from API): treat as local date to avoid timezone shifting.
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const year = Number(iso[1]);
    const monthIndex = Number(iso[2]) - 1;
    const day = Number(iso[3]);
    const d = new Date(year, monthIndex, day);
    return isValidDate(d) ? d : undefined;
  }

  // UK display dates from profile/UI (DD/MM/YYYY) — avoids Date.parse locale ambiguity.
  const dmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const monthIndex = Number(dmy[2]) - 1;
    const year = Number(dmy[3]);
    const d = new Date(year, monthIndex, day);
    return isValidDate(d) ? d : undefined;
  }

  const ts = Date.parse(raw);
  if (!Number.isFinite(ts)) return undefined;
  const d = new Date(ts);
  return isValidDate(d) ? d : undefined;
}

function toLocalDateOnly(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Format a Date as a "YYYY-MM-DD" string using its **local** components.
 *
 * Symmetric counterpart to `parseDobToDate`. Avoids the classic toISOString()
 * bug where local-midnight dates shift back a day in positive UTC offsets
 * (e.g. picking 4 Apr 2010 in BST stores "2010-04-03"). Use this anywhere a
 * Date object from a date picker is being persisted as a date-only string.
 */
export function formatDobAsYYYYMMDD(date: Date): string | undefined {
  if (!isValidDate(date)) return undefined;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Calculate age in **whole years** (floor), or undefined when invalid/future DOB.
 */
export function calculateAge(dob: DobInput, asOf: Date = new Date()): number | undefined {
  const dobDate = parseDobToDate(dob);
  if (!dobDate) return undefined;

  const dobLocal = toLocalDateOnly(dobDate);
  const asOfLocal = toLocalDateOnly(asOf);
  if (!isValidDate(dobLocal) || !isValidDate(asOfLocal)) return undefined;

  // Future DOBs are invalid
  if (dobLocal.getTime() > asOfLocal.getTime()) return undefined;

  let age = asOfLocal.getFullYear() - dobLocal.getFullYear();
  const m = asOfLocal.getMonth() - dobLocal.getMonth();
  if (m < 0 || (m === 0 && asOfLocal.getDate() < dobLocal.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : undefined;
}

export function isAgeAtLeast(dob: DobInput, minAgeYears: number, asOf: Date = new Date()): boolean {
  if (!Number.isFinite(minAgeYears)) return true;
  const age = calculateAge(dob, asOf);
  return typeof age === "number" ? age >= minAgeYears : false;
}

export function isAgeAtMost(dob: DobInput, maxAgeYears: number, asOf: Date = new Date()): boolean {
  if (!Number.isFinite(maxAgeYears)) return true;
  const age = calculateAge(dob, asOf);
  return typeof age === "number" ? age <= maxAgeYears : false;
}

/**
 * Convenience for DOB pickers: derive the allowable DOB range for a (minAge,maxAge) rule.
 *
 * - minDob: earliest DOB allowed (oldest person allowed)
 * - maxDob: latest DOB allowed (youngest person allowed)
 */
export function getDobRangeFromAgeRules(params: {
  minAgeYears?: number;
  maxAgeYears?: number;
  asOf?: Date;
}): { minDob?: Date; maxDob?: Date } {
  const asOf = toLocalDateOnly(params.asOf ?? new Date());
  const out: { minDob?: Date; maxDob?: Date } = {};

  // Latest DOB allowed for minAge: asOf - minAge years (inclusive).
  if (typeof params.minAgeYears === "number" && Number.isFinite(params.minAgeYears)) {
    out.maxDob = new Date(
      asOf.getFullYear() - params.minAgeYears,
      asOf.getMonth(),
      asOf.getDate()
    );
  }

  // Earliest DOB allowed for maxAge (whole-years floor):
  // Must be younger than (maxAge+1) birthday → DOB >= (asOf - (maxAge+1) years + 1 day)
  if (typeof params.maxAgeYears === "number" && Number.isFinite(params.maxAgeYears)) {
    const base = new Date(
      asOf.getFullYear() - (params.maxAgeYears + 1),
      asOf.getMonth(),
      asOf.getDate()
    );
    const minDob = new Date(base);
    minDob.setDate(minDob.getDate() + 1);
    out.minDob = minDob;
  }

  return out;
}